import { useMutation } from "@tanstack/react-query";
import type { CandidateView } from "@portal/schemas";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Input,
  Label,
  NativeSelect,
  NativeSelectOption,
  Textarea,
} from "@portal/ui";
import { useState } from "react";
import { approveCandidate } from "../../api/client";
import { useCustomers, useInvalidate, useProjects } from "../../api/queries";
import { InlineError } from "../../components/InlineError";
import { CREATE_NEW } from "../../components/select-create";
import { CustomerFormDialog } from "../customers/CustomerFormDialog";
import { Check } from "lucide-react";

/**
 * 候補の承認。新規プロジェクトとして登録するか、既存プロジェクトへ紐付ける(SPEC §3.1 / §5.1)。
 * 顧客・プロジェクトの選択肢は既存の一覧 API から取る。
 */
export function ApproveCandidateDialog({ candidate }: { candidate: CandidateView }) {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<"NEW" | "EXISTING">("NEW");
  const [name, setName] = useState(candidate.projectNameHint || candidate.title);
  const [code, setCode] = useState("");
  const [customerId, setCustomerId] = useState("");
  const [projectId, setProjectId] = useState("");
  const [note, setNote] = useState("");
  // 「＋ 新しい顧客を登録…」で開く登録のダイアログ
  const [creatingCustomer, setCreatingCustomer] = useState(false);
  const customers = useCustomers("ACTIVE");
  const projects = useProjects({ status: "ACTIVE" });
  const invalidate = useInvalidate();
  const approve = useMutation({
    mutationFn: () =>
      approveCandidate(
        candidate.id,
        mode === "NEW"
          ? {
              mode: "NEW",
              revision: candidate.revision,
              name,
              ...(code.trim() ? { code } : {}),
              ...(customerId ? { customerId } : {}),
              ...(note.trim() ? { note } : {}),
            }
          : {
              mode: "EXISTING",
              revision: candidate.revision,
              projectId,
              ...(note.trim() ? { note } : {}),
            },
      ),
    onSuccess: async () => {
      await invalidate.candidates();
      setOpen(false);
    },
  });
  const canSubmit = mode === "NEW" ? name.trim() !== "" : projectId !== "";
  const hintCustomer = candidate.customerNameHint
    ? customers.data?.customers.find(
        (c) =>
          c.name === candidate.customerNameHint || c.aliases.includes(candidate.customerNameHint),
      )
    : undefined;

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (next && hintCustomer && !customerId) setCustomerId(hintCustomer.id);
      }}
    >
      <DialogTrigger asChild>
        <Button size="sm">
          <Check />
          承認
        </Button>
      </DialogTrigger>
      <DialogContent>
        <form
          className="flex flex-col gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            approve.mutate();
          }}
        >
          <DialogHeader>
            <DialogTitle>候補を承認</DialogTitle>
            <DialogDescription>{candidate.title}</DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="approve-mode">登録方法</Label>
            <NativeSelect
              id="approve-mode"
              value={mode}
              onChange={(e) => setMode(e.target.value as "NEW" | "EXISTING")}
            >
              <NativeSelectOption value="NEW">新規プロジェクトとして登録</NativeSelectOption>
              <NativeSelectOption value="EXISTING">既存プロジェクトへ紐付け</NativeSelectOption>
            </NativeSelect>
          </div>
          {mode === "NEW" ? (
            <>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="approve-name">プロジェクト名</Label>
                <Input id="approve-name" value={name} onChange={(e) => setName(e.target.value)} />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="approve-code">プロジェクトコード(任意)</Label>
                <Input id="approve-code" value={code} onChange={(e) => setCode(e.target.value)} />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="approve-customer">主顧客(任意)</Label>
                <NativeSelect
                  id="approve-customer"
                  value={customerId}
                  onChange={(e) =>
                    e.target.value === CREATE_NEW
                      ? setCreatingCustomer(true)
                      : setCustomerId(e.target.value)
                  }
                >
                  <NativeSelectOption value="">未設定</NativeSelectOption>
                  {customers.data?.customers.map((c) => (
                    <NativeSelectOption key={c.id} value={c.id}>
                      {c.name}
                    </NativeSelectOption>
                  ))}
                  {/* マスタに無い顧客は、ここから登録してそのまま選べる(ユーザー要望 2026-10-15) */}
                  <NativeSelectOption value={CREATE_NEW}>＋ 新しい顧客を登録…</NativeSelectOption>
                </NativeSelect>
                {candidate.customerNameHint && !hintCustomer && (
                  <p className="text-xs text-muted-foreground">
                    件名から推定した顧客名「{candidate.customerNameHint}」は顧客マスタにありません。
                  </p>
                )}
              </div>
            </>
          ) : (
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="approve-project">紐付け先プロジェクト</Label>
              <NativeSelect
                id="approve-project"
                value={projectId}
                onChange={(e) => setProjectId(e.target.value)}
              >
                <NativeSelectOption value="">選択してください</NativeSelectOption>
                {projects.data?.projects.map((p) => (
                  <NativeSelectOption key={p.id} value={p.id}>
                    {p.name}
                  </NativeSelectOption>
                ))}
              </NativeSelect>
            </div>
          )}
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="approve-note">メモ(任意)</Label>
            <Textarea
              id="approve-note"
              rows={2}
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
          </div>
          <InlineError error={approve.error} />
          <DialogFooter>
            <Button type="submit" disabled={!canSubmit || approve.isPending}>
              <Check />
              {approve.isPending ? "承認中…" : "承認する"}
            </Button>
          </DialogFooter>
        </form>
        {/* 承認のフォームの外に置く(登録のフォームの送信が、承認のフォームへ伝わらないように) */}
        <CustomerFormDialog
          open={creatingCustomer}
          onOpenChange={setCreatingCustomer}
          {...(candidate.customerNameHint && !hintCustomer
            ? { defaultName: candidate.customerNameHint }
            : {})}
          onSaved={(c) => setCustomerId(c.id)}
        />
      </DialogContent>
    </Dialog>
  );
}
